export const REPORT_CATEGORIES = [
  'Clinical',
  'Admissions',
  'Lab',
  'Billing',
  'HR',
  'Admin',
  'Uncategorized',
];

export const REPORT_ROW_SOFT_CAP = 5000;

export function parseFilterDefs(filters) {
  if (!filters) return [];
  if (Array.isArray(filters)) return filters;
  if (typeof filters === 'string') {
    try {
      const parsed = JSON.parse(filters);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }
  return [];
}

export function parseDisplayConfig(display) {
  if (!display) {
    return { columns: {}, grouping: {}, aggregates: {}, defaultSort: [] };
  }
  let config = display;
  if (typeof display === 'string') {
    try {
      config = JSON.parse(display);
    } catch (err) {
      return { columns: {}, grouping: {}, aggregates: {}, defaultSort: [] };
    }
  }
  if (config && config.display) {
    config = config.display;
  }
  return {
    columns: (config && config.columns) || {},
    grouping: (config && config.grouping) || {},
    aggregates: (config && config.aggregates) || {},
    defaultSort: (config && config.defaultSort) || [],
  };
}

export function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const cleaned = typeof value === 'string' ? value.replace(/,/g, '') : value;
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? null : num;
}

export function isNumericColumn(rows, column) {
  return rows.some((row) => toNumber(row[column]) !== null);
}

function compareValues(a, b) {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const aNum = toNumber(a);
  const bNum = toNumber(b);
  if (aNum !== null && bNum !== null) return aNum - bNum;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function sortRows(rows, sortSpec) {
  if (!sortSpec || !sortSpec.length || !rows || !rows.length) return rows || [];
  const specs = sortSpec.filter((spec) => spec && spec.column);
  if (!specs.length) return [...rows];
  return [...rows].sort((left, right) => {
    for (const spec of specs) {
      const result = compareValues(left[spec.column], right[spec.column]);
      if (result !== 0) {
        return spec.direction === 'desc' ? -result : result;
      }
    }
    return 0;
  });
}

export function aggregateValues(values, fn) {
  const numeric = values.map(toNumber).filter((value) => value !== null);
  switch (fn) {
    case 'count':
      return values.filter((value) => value !== null && value !== undefined && value !== '').length;
    case 'sum':
      return numeric.reduce((sum, value) => sum + value, 0);
    case 'avg':
      return numeric.length ? numeric.reduce((sum, value) => sum + value, 0) / numeric.length : null;
    case 'min':
      return numeric.length ? Math.min(...numeric) : null;
    case 'max':
      return numeric.length ? Math.max(...numeric) : null;
    default:
      return null;
  }
}

export function resolveAggregateMap(rows, columns, aggregates) {
  const configured = (aggregates && aggregates.columns) || {};
  if (Object.keys(configured).length) return configured;
  if (!aggregates || !aggregates.grandTotal) return {};
  const inferred = {};
  columns.forEach((column) => {
    if (isNumericColumn(rows, column)) {
      inferred[column] = 'sum';
    }
  });
  return inferred;
}

function buildAggregateRow(rows, columns, aggregateMap, label, firstColumn) {
  const row = {};
  columns.forEach((column) => {
    if (column === firstColumn) {
      row[column] = label;
      return;
    }
    const fn = aggregateMap[column];
    row[column] = fn ? aggregateValues(rows.map((item) => item[column]), fn) : '';
  });
  return row;
}

export function buildTableModel(rows, displayConfig, options = {}) {
  const columns = rows && rows.length ? Object.keys(rows[0]) : [];
  const groupingColumns = (displayConfig.grouping && displayConfig.grouping.columns) || [];
  const showSubtotals = Boolean(displayConfig.grouping && displayConfig.grouping.showSubtotals);
  const aggregateMap = resolveAggregateMap(rows, columns, displayConfig.aggregates);
  const sortSpec = options.sort && options.sort.length ? options.sort : displayConfig.defaultSort;
  const sorted = sortRows(rows || [], sortSpec);
  const collapsedGroups = options.collapsedGroups || {};
  const firstColumn = columns[0];

  if (!groupingColumns.length) {
    const items = sorted.map((row, index) => ({ type: 'data', row, key: `row-${index}` }));
    if (displayConfig.aggregates && displayConfig.aggregates.grandTotal && sorted.length) {
      items.push({
        type: 'grandTotal',
        row: buildAggregateRow(sorted, columns, aggregateMap, 'Grand Total', firstColumn),
        key: 'grand-total',
      });
    }
    return { columns, items, groups: [] };
  }

  const groups = [];
  const groupIndex = new Map();
  sorted.forEach((row, index) => {
    const groupValues = groupingColumns.map((column) => row[column]);
    const groupKey = JSON.stringify(groupValues);
    if (!groupIndex.has(groupKey)) {
      groupIndex.set(groupKey, groups.length);
      groups.push({
        key: groupKey,
        values: groupValues,
        label: groupingColumns
          .map((column, idx) => `${column}: ${groupValues[idx] == null || groupValues[idx] === '' ? '(blank)' : groupValues[idx]}`)
          .join(' · '),
        rows: [],
      });
    }
    groups[groupIndex.get(groupKey)].rows.push({ row, index });
  });

  const items = [];
  groups.forEach((group) => {
    const collapsed = collapsedGroups[group.key] === true;
    items.push({
      type: 'group',
      key: `group-${group.key}`,
      groupKey: group.key,
      label: group.label,
      count: group.rows.length,
      collapsed,
    });
    if (!collapsed) {
      group.rows.forEach((entry) => {
        items.push({ type: 'data', row: entry.row, key: `row-${entry.index}`, groupKey: group.key });
      });
    }
    if (showSubtotals) {
      items.push({
        type: 'subtotal',
        key: `subtotal-${group.key}`,
        groupKey: group.key,
        row: buildAggregateRow(group.rows.map((entry) => entry.row), columns, aggregateMap, `Subtotal (${group.rows.length})`, firstColumn),
      });
    }
  });

  if (displayConfig.aggregates && displayConfig.aggregates.grandTotal && sorted.length) {
    items.push({
      type: 'grandTotal',
      row: buildAggregateRow(sorted, columns, aggregateMap, 'Grand Total', firstColumn),
      key: 'grand-total',
    });
  }

  return { columns, items, groups };
}

export function tableModelToAoa(model, formatCell) {
  const { columns, items } = model;
  const aoa = [columns];
  items.forEach((item) => {
    if (item.type === 'group') {
      const row = columns.map((_, index) => (index === 0 ? `${item.label} (${item.count})` : ''));
      aoa.push(row);
      return;
    }
    aoa.push(columns.map((column) => formatCell(column, item.row[column], item.row, item)));
  });
  return aoa;
}

export function getColumnLink(displayConfig, columnName) {
  const columnConfig = displayConfig.columns && displayConfig.columns[columnName];
  return columnConfig && columnConfig.linkTo ? columnConfig.linkTo : null;
}

export function resolveLinkTarget(linkTo, row) {
  if (!linkTo) return null;
  const type = linkTo.type || (linkTo.reportId ? 'report' : null);
  if (type === 'patient') {
    const field = linkTo.idField || 'patient_id';
    const value = row[field];
    return { type: 'patient', field, value, row };
  }
  if (type === 'admission') {
    const field = linkTo.idField || 'admission_id';
    return { type: 'admission', field, value: row[field], row };
  }
  if (type === 'report') {
    return {
      type: 'report',
      reportId: linkTo.reportId,
      parameterMapping: linkTo.parameterMapping || {},
      row,
    };
  }
  return null;
}

export function mappedReportFilters(parameterMapping, row, currentFilters = {}) {
  const next = { ...currentFilters };
  Object.entries(parameterMapping || {}).forEach(([filterName, columnName]) => {
    next[filterName] = row[columnName];
  });
  return next;
}

export function filtersToSearch(filters) {
  const params = new URLSearchParams();
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function filtersFromSearch(search, filterDefs, defaults = {}) {
  const params = new URLSearchParams(search || '');
  const result = { ...defaults };
  filterDefs.forEach((filter) => {
    if (params.has(filter.name)) {
      result[filter.name] = params.get(filter.name);
    }
  });
  return result;
}

const NAMED_STYLES = {
  highlight: { backgroundColor: '#fff3cd', fontWeight: '600' },
  warning: { backgroundColor: '#fff3cd', color: '#856404' },
  danger: { backgroundColor: '#f8d7da', color: '#721c24', fontWeight: 'bold' },
  success: { backgroundColor: '#d4edda', color: '#155724' },
  info: { backgroundColor: '#d1ecf1', color: '#0c5460' },
};

export function normalizeFormat(format) {
  if (!format) return null;
  if (typeof format === 'string') return { type: format };
  if (typeof format === 'object') return format;
  return null;
}

export function normalizeStyle(style) {
  if (!style) return {};
  if (typeof style === 'string') {
    return NAMED_STYLES[style.toLowerCase()] || {};
  }
  if (typeof style === 'object' && !Array.isArray(style)) return style;
  return {};
}

export function parseWhenExpression(when) {
  if (!when || typeof when !== 'string') return null;
  const match = when.trim().match(/^([A-Za-z_][\w.]*)\s*(<=|>=|!=|==|=|<|>)\s*(.+)$/);
  if (!match) return null;
  let value = match[3].trim();
  if (
    (value.startsWith('"') && value.endsWith('"'))
    || (value.startsWith("'") && value.endsWith("'"))
  ) {
    value = value.slice(1, -1);
  }
  return {
    field: match[1],
    operator: match[2] === '=' ? '==' : match[2],
    value,
  };
}

export function collectStyleRules(columnConfig) {
  if (!columnConfig || !columnConfig.conditionalStyle) return [];
  const styleConfig = columnConfig.conditionalStyle;
  const rules = [];

  if (styleConfig.when) {
    const condition = parseWhenExpression(styleConfig.when);
    if (condition) {
      rules.push({
        condition,
        style: normalizeStyle(styleConfig.style),
        applyToRow: styleConfig.applyTo === 'row' || Boolean(condition.field),
      });
    }
  }

  if (Array.isArray(styleConfig.conditions)) {
    styleConfig.conditions.forEach((rule) => {
      if (!rule || !rule.condition) return;
      rules.push({
        condition: rule.condition,
        style: normalizeStyle(rule.style),
        applyToRow: rule.applyTo === 'row' || Boolean(rule.condition.field),
      });
    });
  }

  if (styleConfig.valueMap && typeof styleConfig.valueMap === 'object') {
    Object.entries(styleConfig.valueMap).forEach(([key, style]) => {
      rules.push({
        condition: { operator: '==', value: key },
        style: normalizeStyle(style),
        applyToRow: false,
      });
    });
  }

  if (styleConfig.style && !styleConfig.when && !styleConfig.conditions && !styleConfig.valueMap) {
    rules.push({
      condition: null,
      style: normalizeStyle(styleConfig.style),
      applyToRow: false,
      always: true,
    });
  }

  return rules;
}

